/**
 * Store Module
 *
 * API methods for store data.
 */

import { forwardFailure, get } from '../core/client';
import { DEFAULT_API_BASE_URL, getConfig, isInitialized, setShopId } from '../core/config';
import { buildEndpoint } from '../core/endpoint';
import type {
  SDKResponse,
  Shop,
  Product,
  StorefrontData,
  ProductGroup,
  Category,
  StorefrontAddonBootstrap,
  CursorPagination,
} from '../types';

interface StoreResponse {
  shop: Shop;
  products?: Product[];
  products_pagination?: CursorPagination | null;
  groups?: ProductGroup[];
  items?: StorefrontData['items'];
  categories?: Category[];
  addons?: StorefrontAddonBootstrap;
}

interface StorefrontResponse {
  shop: Shop;
}

export interface GetStorefrontOptions {
  productsLimit?: number;
  productsCursor?: string | null;
}

const STORE_CACHE_TTL = 5 * 60 * 1000;

export async function getStore(): Promise<SDKResponse<Shop>> {
  const config = getConfig();
  const response = await get<StoreResponse>(
    buildEndpoint('/v1/storefront/shops/name/:storeSlug', {
      storeSlug: config.storeSlug,
    }),
    {
      buyerCurrency: true,
      cache: {
        key: `store:${config.storeSlug}`,
        ttl: STORE_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    // Cache the shopId for slug lookups
    if (response.data.shop?.id) {
      setShopId(response.data.shop.id);
    }
    return {
      success: true,
      data: response.data.shop,
    };
  }

  return forwardFailure(response);
}

export async function resolveStoreByDomain(
  domain?: string,
  apiBaseUrl?: string
): Promise<SDKResponse<Shop>> {
  const resolvedDomain =
    domain ??
    (typeof window !== 'undefined' ? window.location.hostname : '');

  if (!resolvedDomain) {
    return {
      success: false,
      message: 'Domain is required to resolve store',
    };
  }

  const cleanDomain = resolvedDomain
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .trim();

  const baseUrl =
    apiBaseUrl ??
    (isInitialized() ? getConfig().apiBaseUrl : DEFAULT_API_BASE_URL);

  const response = await get<StorefrontResponse>(
    buildEndpoint('/v1/storefront/shops/domain/:domain', {
      domain: cleanDomain,
    }),
    {
      baseUrl,
      buyerCurrency: true,
      cache: {
        key: `store:domain:${cleanDomain}`,
        ttl: STORE_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data?.shop) {
    if (response.data.shop.id) {
      setShopId(response.data.shop.id);
    }
    return {
      success: true,
      data: response.data.shop,
    };
  }

  return forwardFailure(response, { message: response.message ?? 'Failed to resolve store' });
}

export async function getStorefront(options?: GetStorefrontOptions): Promise<SDKResponse<StorefrontData>> {
  const config = getConfig();
  const query = new URLSearchParams();
  if (Number.isFinite(options?.productsLimit)) {
    query.set('products_limit', String(Math.max(1, Math.floor(options?.productsLimit ?? 0))));
  }
  if (typeof options?.productsCursor === 'string' && options.productsCursor.trim().length > 0) {
    query.set('products_cursor', options.productsCursor);
  }
  const querySuffix = query.size > 0 ? `?${query.toString()}` : '';
  const response = await get<StoreResponse>(
    `${buildEndpoint('/v1/storefront/shops/name/:storeSlug', {
      storeSlug: config.storeSlug,
    })}${querySuffix}`,
    {
      buyerCurrency: true,
      cache: {
        key: `storefront:${config.storeSlug}:${options?.productsLimit ?? 'full'}:${options?.productsCursor ?? 'start'}`,
        ttl: STORE_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    // Cache the shopId for slug lookups
    if (response.data.shop?.id) {
      setShopId(response.data.shop.id);
    }
    return {
      success: true,
      data: {
        shop: response.data.shop,
        products: response.data.products ?? [],
        products_pagination: response.data.products_pagination ?? null,
        groups: response.data.groups ?? [],
        items: response.data.items ?? [],
        categories: response.data.categories ?? [],
        addons: response.data.addons ?? { items: [] },
      },
    };
  }

  return forwardFailure(response);
}

export async function getStoreLogoUrl(): Promise<string | null> {
  const response = await getStore();

  if (response.success && response.data?.logo) {
    return response.data.logo;
  }

  return null;
}

export async function getStoreBannerUrl(): Promise<string | null> {
  const response = await getStore();

  if (response.success && response.data?.banner) {
    return response.data.banner;
  }

  return null;
}
